import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, GridIcon, ArrowUpIcon, ArrowDownIcon } from './Icons';
import { OPERATORS, LOGIC_OPERATORS } from '../constants/operators';
import { parseFilterString } from '../utils/xmlHelpers';
import SearchableSelect from './SearchableSelect';

const LightningIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

// Pencil/Edit icon for custom field mode toggle
const EditIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const FilterBuilderModal = ({ isOpen, onClose, fields, currentFilter, onApply, originalSheetName, currentShowPrivate, currentOrderBy }) => {
  const [rows, setRows] = useState([]);
  const [showPrivate, setShowPrivate] = useState(false);
  const [orderByRows, setOrderByRows] = useState([{ field: '', descending: false }]);
  const [customFieldMode, setCustomFieldMode] = useState({}); // Track which rows are in custom field mode
  
  useEffect(() => { 
    if (!isOpen) return; 
    const parsed = parseFilterString(currentFilter || ''); 
    if (parsed.length === 0 || (parsed.length === 1 && !parsed[0].field)) {
      setRows([{ field: '', operator: '=', value: '', value2: '', logic: 'AND' }]);
      setCustomFieldMode({});
    } else {
      setRows(parsed);
      // Detect custom fields (fields not in the dropdown list)
      const fieldNames = fields.map(f => typeof f === 'string' ? f : f.value);
      const customModes = {};
      parsed.forEach((row, i) => {
        if (row.field && !fieldNames.includes(row.field)) {
          customModes[i] = true;
        }
      });
      setCustomFieldMode(customModes);
    }
    setShowPrivate(currentShowPrivate || false);
    
    if (Array.isArray(currentOrderBy) && currentOrderBy.length > 0) {
      setOrderByRows(currentOrderBy.map(o => ({ field: o.field || '', descending: o.descending || false })));
    } else if (currentOrderBy && currentOrderBy.field) {
      setOrderByRows([{ field: currentOrderBy.field, descending: currentOrderBy.descending || false }]);
    } else {
      setOrderByRows([{ field: '', descending: false }]);
    }
  }, [isOpen, currentFilter, currentShowPrivate, currentOrderBy, fields]);
  
  if (!isOpen) return null;
  
  const addRow = () => setRows([...rows, { field: '', operator: '=', value: '', value2: '', logic: 'AND' }]);
  const removeRow = (i) => {
    setRows(rows.filter((_, idx) => idx !== i));
    // Rebuild customFieldMode with shifted indices
    const newCustomModes = {};
    Object.keys(customFieldMode).forEach(key => {
      const idx = parseInt(key);
      if (idx < i) newCustomModes[idx] = customFieldMode[idx];
      else if (idx > i) newCustomModes[idx - 1] = customFieldMode[idx];
    });
    setCustomFieldMode(newCustomModes);
  };
  const updateRow = (i, k, v) => { const n = [...rows]; n[i][k] = v; setRows(n); };
  const toggleCustomFieldMode = (i) => {
    setCustomFieldMode(prev => ({ ...prev, [i]: !prev[i] }));
    // Clear field when toggling modes
    updateRow(i, 'field', '');
  };
  
  const linkExcelRange = async (index) => {
    try {
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("address");
        await context.sync();
        const newRows = [...rows];
        newRows[index].value = `REF(${range.address})`;
        newRows[index].operator = 'IN';
        setRows(newRows);
      });
    } catch (err) {
      console.error("Excel Link Error:", err);
    }
  };
  
  const addOrderByRow = () => setOrderByRows([...orderByRows, { field: '', descending: false }]);
  const removeOrderByRow = (i) => {
    if (orderByRows.length === 1) {
      setOrderByRows([{ field: '', descending: false }]);
    } else {
      setOrderByRows(orderByRows.filter((_, idx) => idx !== i));
    }
  };
  const updateOrderByRow = (i, k, v) => { const n = [...orderByRows]; n[i][k] = v; setOrderByRows(n); };
  const moveOrderByRow = (i, direction) => {
    const newIdx = direction === 'up' ? i - 1 : i + 1;
    if (newIdx < 0 || newIdx >= orderByRows.length) return;
    const newRows = [...orderByRows];
    [newRows[i], newRows[newIdx]] = [newRows[newIdx], newRows[i]];
    setOrderByRows(newRows);
  };
  
  const handleApply = async () => {
    let query = '';
    rows.forEach((r, index) => {
      if (!r.field) return;
      if (r.operator === 'BETWEEN' && (!r.value || !r.value2)) return;
      if (r.operator !== 'BETWEEN' && r.operator !== 'IS NULL' && r.operator !== 'IS NOT NULL' && !r.value) return;
      
      const quote = (v) => {
        const isCleanNumber = /^-?\d*\.?\d+$/.test(v);
        return isCleanNumber ? v : `'${v}'`;
      };
      
      let clause = '';
      if (r.operator === 'IS NULL') {
        clause = `${r.field} IS NULL`;
      } else if (r.operator === 'IS NOT NULL') {
        clause = `${r.field} IS NOT NULL`;
      } else if (r.operator === 'BETWEEN') {
        clause = `${r.field} >= ${quote(r.value)} AND ${r.field} <= ${quote(r.value2)}`;
      } else if (r.operator === 'IN' || r.operator === 'NOT IN') {
        if (r.value.startsWith('REF(')) {
          clause = `${r.field} ${r.operator} ${r.value}`;
        } else {
          const values = r.value.split(',').map(s => `'${s.trim()}'`).join(',');
          clause = `${r.field} ${r.operator} (${values})`;
        }
      } else {
        let val = r.value, op = r.operator;
        if (op === 'LIKE_START') { op = 'LIKE'; val = `${val}%`; }
        if (op === 'LIKE_CONTAINS') { op = 'LIKE'; val = `%${val}%`; }
        if (op === 'NOT_LIKE_CONTAINS') { op = 'NOT LIKE'; val = `%${val}%`; }
        const finalVal = (op === 'LIKE' || op === 'NOT LIKE') ? `'${val}'` : quote(r.value);
        clause = `${r.field} ${op} ${finalVal}`;
      }
      
      if (index > 0) query += ` ${rows[index - 1].logic || 'AND'} `;
      query += clause;
    });
    
    if (originalSheetName) {
      try {
        await Excel.run(async (context) => {
          context.workbook.worksheets.getItem(originalSheetName).activate();
          await context.sync();
        });
      } catch (err) {
        console.error(err);
      }
    }
    
    const validOrderBy = orderByRows.filter(o => o.field);
    const orderBy = validOrderBy.length > 0 ? validOrderBy : null;
    
    onApply(query, showPrivate, orderBy); 
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] z-50 relative border border-gray-700">
        <div className="p-4 border-b border-gray-700 bg-gray-900 rounded-t-lg flex items-center gap-2">
          <LightningIcon className="w-5 h-5 text-yellow-400" />
          <h3 className="font-bold text-white">Filter Builder</h3>
        </div>
        <div className="p-4 overflow-y-auto min-h-[200px]">
          {/* Filter Conditions */}
          <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Filter Conditions</label>
          {rows.map((r, i) => (
            <div key={i} className="flex flex-col mb-2">
              <div className="flex gap-2 items-center">
                <div className="w-1/3 flex gap-1">
                  {customFieldMode[i] ? (
                    <input 
                      className="flex-1 p-2 border border-gray-600 rounded text-sm bg-gray-700 text-white placeholder-gray-500"
                      placeholder="OBJECT.FIELD..."
                      value={r.field}
                      onChange={e => updateRow(i, 'field', e.target.value)}
                    />
                  ) : (
                    <div className="flex-1">
                      <SearchableSelect 
                        options={fields} 
                        value={r.field} 
                        onChange={(val) => updateRow(i, 'field', val)} 
                        placeholder="Select field..." 
                        zIndex={100 - i} 
                      />
                    </div>
                  )}
                  <button 
                    onClick={() => toggleCustomFieldMode(i)} 
                    className={`p-2 rounded ${customFieldMode[i] ? 'text-yellow-400 bg-gray-600' : 'text-gray-500 hover:text-yellow-400'}`}
                    title={customFieldMode[i] ? "Switch to dropdown" : "Enter custom field (e.g., ARINVOICE.RECORDID)"}
                  >
                    <EditIcon />
                  </button>
                </div>
                <select 
                  className="w-1/4 p-2 border border-gray-600 rounded text-sm bg-gray-700 text-white" 
                  value={r.operator} 
                  onChange={e => updateRow(i, 'operator', e.target.value)}
                >
                  {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {r.operator === 'BETWEEN' ? (
                  <div className="flex-1 flex gap-2 items-center">
                    <input className="w-1/2 p-2 border border-gray-600 rounded text-sm bg-gray-700 text-white" placeholder="Start..." value={r.value} onChange={e => updateRow(i, 'value', e.target.value)} />
                    <span className="text-xs font-bold text-gray-500">AND</span>
                    <input className="w-1/2 p-2 border border-gray-600 rounded text-sm bg-gray-700 text-white" placeholder="End..." value={r.value2} onChange={e => updateRow(i, 'value2', e.target.value)} />
                  </div>
                ) : r.operator === 'IS NULL' || r.operator === 'IS NOT NULL' ? (
                  <div className="flex-1 p-2 text-sm text-gray-500 italic">
                    No value needed
                  </div>
                ) : (
                  <div className="relative flex-1">
                    <input 
                      className="w-full p-2 border border-gray-600 rounded text-sm pr-8 bg-gray-700 text-white placeholder-gray-500" 
                      placeholder={r.operator.includes('IN') ? "Val1, Val2..." : "Value..."} 
                      value={r.value} 
                      onChange={e => updateRow(i, 'value', e.target.value)} 
                    />
                    <button onClick={() => linkExcelRange(i)} className="absolute right-2 top-2 text-gray-500 hover:text-green-400" title="Link to Excel Selection">
                      <GridIcon />
                    </button>
                  </div>
                )}
                <button onClick={() => removeRow(i)} className="p-2 text-gray-500 hover:text-red-400" disabled={rows.length === 1}>
                  <TrashIcon />
                </button>
              </div>
              {i < rows.length - 1 && (
                <div className="flex justify-center my-1">
                  <div className="bg-gray-700 rounded p-1 flex gap-1 border border-gray-600">
                    {LOGIC_OPERATORS.map(op => (
                      <button 
                        key={op.value} 
                        onClick={() => updateRow(i, 'logic', op.value)} 
                        className={`px-3 py-0.5 text-[10px] font-bold rounded ${r.logic === op.value ? 'bg-yellow-500 text-gray-900' : 'text-gray-400 hover:bg-gray-600'}`}
                      >
                        {op.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <button onClick={addRow} className="flex items-center text-sm text-green-400 font-semibold hover:underline mt-2 mb-4">
            <span className="mr-1"><PlusIcon /></span> Add Condition
          </button>
          
          <div className="border-t border-gray-700 my-4"></div>
          
          {/* Order By Section */}
          <div className="mb-4">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Order By</label>
            {orderByRows.map((orderRow, i) => (
              <div key={i} className="flex gap-2 items-center mb-2">
                <div className="flex-1">
                  <SearchableSelect 
                    options={fields} 
                    value={orderRow.field} 
                    onChange={(val) => updateOrderByRow(i, 'field', val)} 
                    placeholder="Select field to sort by..."
                    zIndex={50 - i}
                  />
                </div>
                <div className="flex bg-gray-700 rounded p-0.5 border border-gray-600">
                  <button 
                    onClick={() => updateOrderByRow(i, 'descending', false)}
                    disabled={!orderRow.field}
                    className={`px-3 py-1.5 text-xs font-bold rounded ${!orderRow.descending ? 'bg-yellow-500 text-gray-900' : 'text-gray-400'} ${!orderRow.field ? 'opacity-50' : ''}`}
                  >
                    ASC ↑
                  </button>
                  <button 
                    onClick={() => updateOrderByRow(i, 'descending', true)}
                    disabled={!orderRow.field}
                    className={`px-3 py-1.5 text-xs font-bold rounded ${orderRow.descending ? 'bg-yellow-500 text-gray-900' : 'text-gray-400'} ${!orderRow.field ? 'opacity-50' : ''}`}
                  >
                    DESC ↓
                  </button>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => moveOrderByRow(i, 'up')} disabled={i === 0} className="p-1.5 text-gray-500 hover:text-yellow-400 disabled:opacity-30" title="Move up">
                    <ArrowUpIcon />
                  </button>
                  <button onClick={() => moveOrderByRow(i, 'down')} disabled={i === orderByRows.length - 1} className="p-1.5 text-gray-500 hover:text-yellow-400 disabled:opacity-30" title="Move down">
                    <ArrowDownIcon />
                  </button>
                </div>
                <button onClick={() => removeOrderByRow(i)} className="p-2 text-gray-500 hover:text-red-400" title="Remove">
                  <TrashIcon />
                </button>
              </div>
            ))}
            <button onClick={addOrderByRow} className="flex items-center text-sm text-green-400 font-semibold hover:underline mt-2">
              <span className="mr-1"><PlusIcon /></span> Add Sort Field
            </button>
          </div>
          
          {/* Options Section */}
          <div className="bg-gray-700 rounded p-3 border border-gray-600">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Options</label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input 
                type="checkbox" 
                checked={showPrivate} 
                onChange={(e) => setShowPrivate(e.target.checked)} 
                className="rounded text-yellow-500 w-4 h-4 bg-gray-600 border-gray-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-200">Include Private Entities</span>
                <p className="text-xs text-gray-500">Show data from private/child entities in multi-entity environments</p>
              </div>
            </label>
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 bg-gray-900 flex justify-end gap-2 rounded-b-lg">
          <button onClick={onClose} className="px-4 py-2 text-gray-300 font-medium hover:bg-gray-700 rounded">Cancel</button>
          <button onClick={handleApply} className="px-5 py-2 bg-green-500 text-gray-900 rounded font-medium shadow-sm hover:bg-green-400">⚡ Apply Filter</button>
        </div>
      </div>
    </div>
  );
};

export default FilterBuilderModal;